/**
 * ConfigDriftPanel -- field-level "what is out of sync" on the device Configuration section
 * (OP#1783).
 *
 * Before: the device page showed a "Diverged" pill plus at most five raw JSON lines in the
 * identity rail; nothing said what was compared and agreed, what a device can never report, or
 * which operational values are deliberately ignored. After: a per-field table (field, Meridian
 * intends, device reports), the matching fields, the regions no device reports, and the ignore
 * list -- all from GET /devices/{serial}/config/drift.
 *
 * READ-ONLY by design. No re-push / adopt buttons here: those are device writes and belong on
 * the plan -> confirm -> apply machinery (separate change). Secrets arrive masked from the
 * server for every role; a masked cell says so instead of looking like a real value.
 *
 * Design system only: CardList (DataGrid's reflow form; the sheet is too narrow for a 3-column
 * grid), md-card, StatusPill for the verdict, ink / ink-muted / ink-subtle text on bg-surface /
 * surface-2 with border-line, so light and dark both come from the tokens.
 */
import { useDeviceConfigDrift } from "@/lib/api/hooks";
import { CardList } from "@/components/data/CardList";
import { StatusPill } from "@/components/ui/StatusPill";
import { ErrorBanner } from "@/components/ui/Page";
import {
  containsMask, driftHeadline, formatDriftValue, type ConfigDrift, type DriftRow,
} from "@/lib/configDrift";
import { relTime } from "@/lib/format";

/** A desired/actual cell: monospace value, or an explicit "hidden (secret)" when masked. */
function ValueCell({ value, tone }: { value: unknown; tone: "intended" | "reported" }) {
  if (containsMask(value)) {
    return (
      <span className="text-xs italic text-ink-subtle" title="Secret values are never shown, for any role.">
        hidden (secret)
      </span>
    );
  }
  return (
    <span className={`break-all font-mono text-xs ${tone === "reported" ? "text-critical" : "text-ink"}`}>
      {formatDriftValue(value)}
    </span>
  );
}

/**
 * One drifted field as a card: field name (raw path beneath when it differs), then intended and
 * reported values on their own labelled lines. Cards, not a 3-column grid: the device sheet is
 * ~390px wide at every breakpoint, and a table there clipped the "Device reports" column (the
 * one the operator came for) behind a horizontal scroll. CardList is DataGrid's own reflow form.
 */
function DriftCard(r: DriftRow) {
  return (
    <div className="flex flex-col gap-1.5" data-testid="config-drift-row">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-ink">{r.label}</span>
        {r.label !== r.path && <span className="font-mono text-[0.6875rem] text-ink-subtle">{r.path}</span>}
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        <dt className="text-ink-muted">Meridian intends</dt>
        <dd><ValueCell value={r.desired} tone="intended" /></dd>
        <dt className="text-ink-muted">Device reports</dt>
        <dd><ValueCell value={r.actual} tone="reported" /></dd>
      </dl>
      {r.note && <span className="text-[0.6875rem] text-ink-muted">{r.note}</span>}
    </div>
  );
}

function verdictPill(d: ConfigDrift) {
  if (d.verdict === "unknown") return <StatusPill status="unknown">Not verified</StatusPill>;
  // Offline: the last word, not live state -> neutral, never the green/red ramp.
  if (d.stale) return <StatusPill status="unknown" hollow>Last known</StatusPill>;
  if (d.verdict === "diverged") return <StatusPill status="critical">Out of sync</StatusPill>;
  return <StatusPill status="healthy">In sync</StatusPill>;
}

export function ConfigDriftPanel({ serial }: { serial: string }) {
  const { data, isLoading, error } = useDeviceConfigDrift(serial);
  if (error) return <ErrorBanner error={error} />;
  if (isLoading || !data) {
    return <div className="text-sm text-ink-muted">Comparing intended and reported config…</div>;
  }
  return (
    <div className="mb-3 flex flex-col gap-3 rounded-md border border-line bg-surface-2 p-3" data-testid="config-drift">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-ink">Config drift</span>
        {verdictPill(data)}
        <span className="text-[0.6875rem] text-ink-subtle">Read-only: nothing is pushed from here.</span>
      </div>
      <p className="text-sm text-ink" data-testid="config-drift-headline">{driftHeadline(data)}</p>
      {data.verdict === "diverged" && data.drift_count === 0 && data.detail && (
        <p className="text-xs text-ink-muted">{data.detail}</p>
      )}
      {data.rows.length > 0 && (
        <CardList<DriftRow> rows={data.rows} rowKey={(r) => r.path} renderCard={DriftCard} />
      )}
      {data.in_sync.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-semibold text-ink-muted">Compared and matching</div>
          <ul className="flex flex-wrap gap-1.5" data-testid="config-drift-in-sync">
            {data.in_sync.map((f) => (
              <li key={f.path} className="rounded border border-line bg-surface px-2 py-0.5 text-xs text-ink" title={f.path}>
                {f.label}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(data.not_observable.length > 0 || data.ignored.length > 0) && (
        <details className="text-xs text-ink-muted">
          <summary className="cursor-pointer text-ink-muted hover:text-ink">What is not compared</summary>
          <div className="mt-2 flex flex-col gap-2">
            <div>
              <div className="font-semibold text-ink-muted">Never reported by a device (cannot drift-check)</div>
              <ul className="ml-4 list-disc">
                {data.not_observable.map((r) => <li key={r.region}>{r.label}</li>)}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-ink-muted">Operational values, ignored on purpose</div>
              <ul className="ml-4 list-disc" data-testid="config-drift-ignored">
                {data.ignored.map((r) => <li key={r.name}>{r.reason}</li>)}
              </ul>
            </div>
          </div>
        </details>
      )}
      {data.checked_at && (
        <span className="text-[0.6875rem] text-ink-subtle">
          Compared {relTime(data.last_checked_at ?? data.checked_at)} against the state the device last reported.
        </span>
      )}
    </div>
  );
}
